import { borrowMarketsQueryParams } from '@/config/query/queryParams'
import { type BorrowMarketsQueryParams, getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'

const borrowMarketsQueryKey = ['borrowMarkets'] as const
const DEFAULT_BORROW_MARKETS_PER_PAGE = 1_000

async function fetchAllBorrowMarkets(params: BorrowMarketsQueryParams = {}) {
  const { network, perPage, minTvl } = { ...borrowMarketsQueryParams, ...params }
  const internalAppSdk = getInternalAppSdk()
  const effectivePerPage = perPage ?? DEFAULT_BORROW_MARKETS_PER_PAGE
  const borrowMarkets = []
  const fetchedPages = new Set<number>()
  let page: number | undefined = 0

  while (page !== undefined) {
    if (fetchedPages.has(page)) {
      throw new Error('Failed to fetch borrow markets: repeated pagination cursor')
    }

    fetchedPages.add(page)

    const response = await internalAppSdk.getBorrowMarkets({ network, page, perPage: effectivePerPage, minTvl })
    borrowMarkets.push(...response.data)
    if (response.nextPage !== undefined && response.nextPage <= page) {
      throw new Error('Failed to fetch borrow markets: non-advancing pagination cursor')
    }

    page = response.nextPage
  }

  return borrowMarkets
}

export function borrowMarketsQueryOptions(params: BorrowMarketsQueryParams = {}) {
  const { network, perPage, minTvl } = { ...borrowMarketsQueryParams, ...params }

  return queryOptions({
    queryKey: [...borrowMarketsQueryKey, { network, perPage: perPage ?? DEFAULT_BORROW_MARKETS_PER_PAGE, minTvl }],
    queryFn() {
      return fetchAllBorrowMarkets({ network, perPage, minTvl })
    },
    retry: 2,
    staleTime: 30 * MINUTE_IN_MILLISECONDS,
    refetchInterval: 30 * MINUTE_IN_MILLISECONDS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useBorrowMarketsQuery({
  initialData,
  ...params
}: BorrowMarketsQueryParams & {
  initialData?: ReturnType<typeof borrowMarketsQueryOptions>['initialData']
}) {
  const { data, isFetching } = useQuery({
    ...borrowMarketsQueryOptions(params),
    initialData,
  })

  return { data, isFetching }
}
